import type { IDisplayItem } from "@kuika/kuika-cl-model";
import { CommonHelperFunctions } from "@kuika/kuika-cl-model";
import { refreshAccessToken } from "../../../auth/token-refresh";
import {
  DESIGNER_LIVE_CLIENT_ID,
  DESIGNER_LIVE_CLIENT_ID_HEADER
} from "../../../services/designer-live/designer-live-client-id";
import type { ScreenDesignInstance } from "../designer-context";
import { getSdInstance } from "../designer-context";
export { getSdInstance };

export function buildTreeNode(di: any): any {
  const node: any = {
    kuikaId: di.id?.toString(),
    name: di.item?.name,
    componentType: di.item?.name,
    friendlyName: di.friendlyName || undefined,
    // Screen-contract channels — omitted when unset, so a channel-less screen's
    // tree is byte-identical to before.
    contractId: di.contractId || undefined,
    dataRef: di.dataRef || undefined,
    fieldRef: di.fieldRef || undefined,
    interactionRef: di.interactionRef || undefined,
    childCount: di.children?.length || 0
  };

  if (di.children && di.children.length > 0) {
    node.children = di.children.map((child: any) => buildTreeNode(child));
  }

  return node;
}

export function buildTextTree(displayItems: any[], screenName: string, maxDepth: number = 10): string {
  const lines: string[] = [`Screen: "${screenName}"`];

  function walk(di: any, depth: number) {
    const indent = "  ".repeat(depth);
    const name = di.item?.name || "Unknown";
    const friendly = di.friendlyName ? ` "${di.friendlyName}"` : "";
    const id = di.id != null ? ` [${di.id}]` : "";
    lines.push(`${indent}${name}${friendly}${id}`);

    const children = di.children;
    if (children && children.length > 0) {
      if (depth >= maxDepth) {
        lines.push(`${indent}  ...${children.length} children`);
      } else {
        for (const child of children) {
          walk(child, depth + 1);
        }
      }
    }
  }

  for (const di of displayItems) {
    walk(di, 0);
  }

  return lines.join("\n");
}

interface PropertySummaryItem {
  name: string;
  type: number;
  isBound: boolean;
}

export function getPropertySummary(di: IDisplayItem, sd?: ScreenDesignInstance): PropertySummaryItem[] {
  if (!di.item?.properties) return [];

  // Look up component definition for isTechnical flags
  let componentDefProperties: Array<{ name?: string; isTechnical?: boolean; isDeprecated?: boolean }> | null = null;
  if (sd) {
    const componentDefs = sd.projectProvider.state.project?.componentSetObj?.components;
    if (componentDefs) {
      const componentDef = componentDefs.find((c) => c.id === di.item.id);
      componentDefProperties = componentDef?.properties || null;
    }
  }

  return di.item.properties
    .filter((prop) => {
      if (prop.selectedType === 3) return false;
      if (prop.isDeprecated === true) return false;
      if (componentDefProperties) {
        const defProp = componentDefProperties.find((p) => p.name === prop.name);
        if (defProp?.isTechnical === true || defProp?.isDeprecated === true) return false;
      }
      return true;
    })
    .map((prop) => ({
      name: prop.name,
      type: prop.selectedType,
      isBound: prop.symbol?.category !== undefined && prop.symbol?.category !== 4
    }));
}

export function getAvailableEvents(di: any): string[] {
  if (!di.item?.properties) return [];
  return di.item.properties.filter((p: any) => p.selectedType === 3).map((p: any) => p.name);
}

export function getEventSummary(di: any): any[] {
  if (!di.item?.events) return [];
  return di.item.events.map((evt: any) => ({
    eventName: evt.eventName,
    actionCount: evt.actions?.length || 0
  }));
}

export function getBackendUrl(): string {
  return CommonHelperFunctions.GetBackendUrl();
}

export function getAuthToken(): string | null {
  return localStorage.getItem("tokenString");
}

export function resolveDomainNameHeader(pathname: string, currentDomain?: string): string {
  const urlItems = pathname.substring(1).split(/\//);
  const pathDomain = urlItems[0] || "";
  const normalizedPath = pathDomain.toLowerCase();

  // Match the main axios interceptor behavior for auth/public routes.
  if (!pathDomain || normalizedPath === "login" || normalizedPath === "confirm-email") {
    return "";
  }

  return currentDomain || pathDomain;
}

function getCurrentDomainHeader(): string {
  return resolveDomainNameHeader(
    window.location.pathname,
    (window as unknown as { currentDomain?: string }).currentDomain
  );
}

/**
 * Refresh the auth token and return fresh request headers; empty object on failure.
 *
 * Delegates to auth/token-refresh so this shares the single in-flight refresh with
 * the axios interceptor and the streaming helper — the backend rotates refresh
 * tokens, so a private POST here would retire a token another path is still using.
 */
export async function refreshAuthAndGetHeaders(): Promise<Record<string, string>> {
  try {
    await refreshAccessToken();
    return getRequestHeaders();
  } catch {
    // Token refresh failed — caller decides what to do without auth.
    return {};
  }
}

/**
 * Resolve an `@alias` or `$N.M` kuikaId reference emitted by the server-side
 * ui_render translator into the real numeric kuikaId that the current screen
 * dictionary uses. Mirrors the resolution the `ui_structure_configure_components`
 * handler already performs, so render-expanded `ui_property` / `ui_action`
 * calls can find the component by alias even when set_datasource_bindings
 * or set_multiple_bindings runs against a freshly-added repeater.
 *
 * Returns the input unchanged when it is already numeric or not resolvable;
 * the downstream handler surfaces a clearer "not found" error in that case.
 */
export function resolveKuikaIdRef(kuikaId: string | undefined | null): string {
  if (typeof kuikaId !== "string" || kuikaId.length === 0) return String(kuikaId ?? "");

  if (kuikaId.startsWith("@")) {
    const aliasMap: Record<string, string> = (window as any).kuika?._mcpLastAliasMap || {};
    const dotMatch = kuikaId.match(/^@([^.]+)\.(\d+)$/);
    if (dotMatch) {
      const parentAlias = dotMatch[1];
      const childIdx = parseInt(dotMatch[2], 10);
      const parentId = aliasMap[parentAlias];
      if (parentId) {
        try {
          const sd = getSdInstance();
          const dict = sd.screenProvider?.state?.screenDesignDictionary;
          const parentDi = dict?.[parseInt(parentId, 10)];
          if (parentDi?.children && parentDi.children[childIdx]) {
            const childId = parentDi.children[childIdx].id?.toString();
            if (childId) return childId;
          }
        } catch {
          // fall through to return original
        }
      }
      return kuikaId;
    }
    const aliasName = kuikaId.slice(1);
    const resolved = aliasMap[aliasName];
    return resolved ?? kuikaId;
  }

  if (kuikaId.startsWith("$")) {
    const refMap: Record<string, string> = (window as any).kuika?._mcpLastRefMap || {};
    return refMap[kuikaId] ?? kuikaId;
  }

  return kuikaId;
}

export function getRequestHeaders(): Record<string, string> {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json"
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
    headers.domainName = getCurrentDomainHeader();

    const kuikaWindow = window as unknown as { kuika?: { activeProjectId?: string } };
    if (kuikaWindow.kuika?.activeProjectId) {
      headers.currentProjectId = kuikaWindow.kuika.activeProjectId;
    }
  }
  const userInfoWindow = window as unknown as { userInfo?: { id?: string } };
  if (userInfoWindow.userInfo?.id) {
    headers["x-clientid"] = userInfoWindow.userInfo.id;
  }
  // KOA-18196: every header builder must stamp the per-TAB designer-live origin id —
  // a mutation sent without it publishes with a null origin and the sender toasts its
  // own change back at itself (today this path only hits /auth/refreshtoken, but the
  // omission is a latent trap for any future fetch-based mutation).
  headers[DESIGNER_LIVE_CLIENT_ID_HEADER] = DESIGNER_LIVE_CLIENT_ID;
  return headers;
}

export const DEFAULT_LAYOUT =
  '{"id":-1,"itemType":1,"styleObj":{"fill":{"fillIds":["b469ca57-a59d-48ae-847f-592fc99a7b99"],"fillId": null,"repeatSwitch": false,"bgImageSize": false,"stretch": false,"bgImagePosition": "left"}},"item":{"id":"HTML_DIV","properties":[{"name":"style","selectedType":2,"value":""}]},"children":[],"screenDesign":"[Circular ~]"}';

export function waitForScreenReady(targetSdId: string, oldDictRef: any, timeoutMs: number = 15000): Promise<boolean> {
  return new Promise((resolve) => {
    const pollInterval = 100;
    let elapsed = 0;

    const timer = setInterval(() => {
      elapsed += pollInterval;

      try {
        const sd = getSdInstance();
        const screenState = sd.screenProvider?.state;
        const currentSdId = screenState?.sdId;
        const currentDict = screenState?.screenDesignDictionary;

        if (
          currentSdId === targetSdId &&
          currentDict !== oldDictRef &&
          currentDict &&
          Object.keys(currentDict).length > 0
        ) {
          clearInterval(timer);
          resolve(true);
          return;
        }
      } catch {
        // getSdInstance may throw if designer is mid-transition; keep polling
      }

      if (elapsed >= timeoutMs) {
        clearInterval(timer);
        resolve(false);
      }
    }, pollInterval);
  });
}
